'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useListAssetsInfiniteQuery } from '@web/gen/service/creativefabrica/social/v2'
import { IconClose, IconLink, IconLinkOff } from '@repo/design-system-cf/components/icons'
import debounce from 'lodash/debounce'
import { Button } from '@repo/design-system-cf/components/button'
import { SearchInput } from './search-input'
import * as Popover from 'primitives/popover'
import { Z_INDEX_MODALS } from 'constants/modals'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { useCreatePostProjectDialog } from '../create-post-project-dialog-context'
import { useProjectDraft } from '../project-draft-context'
import { tw } from 'utils/classnames'
import { SkeletonItem } from 'components/skeleton-item'

export function AssetSection() {
  const t = useTranslations('components.create_product_project_dialog.assets_section')
  const { openMobileView } = useCreatePostProjectDialog()
  const { assets: selectedAssets, setAssets: setSelectedAssets } = useProjectDraft()

  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null)

  const [isOpen, setIsOpen] = React.useState(false)
  const [searchText, setSearchText] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')

  const debouncedSetSearch = React.useMemo(
    () => debounce((v: string) => setDebouncedSearch(v), 400),
    [],
  )

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useListAssetsInfiniteQuery({
      text: debouncedSearch,
    })

  const listAssets = data?.pages.flatMap(page => page.data || []) ?? []

  function highlightMatch(text: string, query: string) {
    if (!query) {
      return text
    }
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${escapedQuery})`, 'ig')
    return text.replace(regex, '<strong>$1</strong>')
  }

  React.useEffect(() => {
    debouncedSetSearch(searchText)
  }, [searchText, debouncedSetSearch])

  React.useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  React.useEffect(() => {
    if (!isOpen) {
      return
    }

    const timer = setTimeout(() => {
      const root = scrollContainerRef.current
      const sentinel = loadMoreRef.current

      if (!root || !sentinel) {
        return
      }

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
          }
        },
        { root, threshold: 0.1 },
      )

      observer.observe(sentinel)
    }, 0)

    return () => clearTimeout(timer)
  }, [isOpen, listAssets.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="flex flex-col gap-2 relative" data-testid="selected-asset-container">
      {selectedAssets.map((asset, index) => (
        <div
          key={`${asset.id}_${index}`}
          data-testid={`selected-asset-item-${asset.id}`}
          className="group flex items-center gap-3 bg-cf-white border border-[#1313151F] rounded-2 p-2 justify-between"
        >
          <div className="flex gap-3 items-center">
            <Image
              src={asset.thumbnailUrl || ''}
              alt={asset.name}
              width={53}
              height={40}
              className="w-14 h-10 object-cover rounded border border-[#1313151F]"
            />
            <p data-testid={`selected-asset-item-name-${asset.id}`} className="text-cf-grey-800">
              {asset.name}
            </p>
          </div>

          <Button
            type="button"
            data-testid={`selected-asset-item-delete-button-${asset.id}`}
            variant="tertiary"
            className="bg-[#E5DEF5] rounded-2 size-7 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
            size="small"
            aria-label="delete image"
            onClick={() => {
              setSelectedAssets(prev => prev.filter((_, i) => i !== index))
            }}
            iconRight={<IconClose className="text-cf-purple-900" />}
          />
        </div>
      ))}

      {selectedAssets.length < 5 ? (
        <>
          {/* mobile trigger */}
          {listAssets.length > 0 ? (
            <div
              className="border border-dashed border-[#1313151F] bg-cf-grey-50 rounded-2 p-2 flex gap-3 items-center cursor-pointer sm:hidden hover:bg-cf-grey-100"
              onClick={() => {
                openMobileView({ type: 'assets' })
              }}
            >
              <div className="border border-[#1313151F] bg-cf-grey-25 rounded-2 w-16 h-[42px] flex justify-center items-center">
                <IconLink className="text-cf-purple-900 !size-5" />
              </div>
              <p className="text-cf-grey-600">{t('assets_default_title')}</p>
            </div>
          ) : null}

          {/* desktop popover */}
          <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
            <Popover.Trigger disabled={listAssets.length === 0}>
              {listAssets.length === 0 ? (
                <AssetEmpty isPending={isPending} />
              ) : (
                <div
                  data-testid="asset-section-trigger-desktop"
                  className="hidden sm:flex border border-dashed border-[#1313151F] bg-cf-grey-50 rounded-2 p-2 gap-3 items-center cursor-pointer hover:bg-cf-grey-100"
                >
                  <div className="border border-[#1313151F] bg-cf-grey-25 rounded-2 w-16 h-[42px] flex justify-center items-center">
                    <IconLink className="text-cf-purple-900 !size-5" />
                  </div>
                  <p className="text-cf-grey-600">{t('assets_default_title')}</p>
                </div>
              )}
            </Popover.Trigger>

            <Popover.Portal>
              <Popover.Content
                sideOffset={-58}
                align="start"
                side="top"
                className="sm:w-[479px] rounded-2 bg-cf-white shadow-[0px_4px_8px_3px_rgba(19,19,21,0.16),_0px_1px_2px_0px_rgba(19,19,21,0.12)]"
                style={{ zIndex: Z_INDEX_MODALS.default + 10 }}
                onWheel={e => e.stopPropagation()}
              >
                <div data-testid="asset-section-popover-container">
                  <div className="flex flex-row items-center gap-2 p-4">
                    <SearchInput
                      className="flex-1"
                      value={searchText}
                      onChange={setSearchText}
                      placeholder="Search for an asset"
                    />
                  </div>

                  <p className="text-12 text-cf-grey-600 border-t border-[#1313151F] px-4 py-3">
                    {t('assets_hint_text_mobile')}
                  </p>

                  <div
                    data-testid="asset-section-results-container"
                    ref={scrollContainerRef}
                    className="max-h-[260px] overflow-y-auto py-2"
                  >
                    {debouncedSearch && !isFetchingNextPage && listAssets.length === 0 ? (
                      <div className="px-4 py-2 text-14 text-cf-grey-600">
                        {t('assets_search_no_results')}
                      </div>
                    ) : null}
                    {listAssets.map((asset, index) => {
                      const isSelected = selectedAssets.some(a => a.id === asset.id)

                      return (
                        <div
                          data-testid={`asset-section-search-result-asset-item-${asset.id}`}
                          key={`${asset.id}_${index}`}
                          className={tw(
                            'flex flex-row items-center gap-3 mb-3 px-4 py-1',
                            isSelected
                              ? 'opacity-40 cursor-not-allowed pointer-events-none'
                              : 'cursor-pointer hover:bg-cf-purple-50 active:opacity-75',
                          )}
                          onClick={() => {
                            if (isSelected) {
                              return
                            }
                            setSelectedAssets(prev => [
                              ...prev,
                              {
                                id: asset.id ?? '',
                                name: asset.name ?? '',
                                thumbnailUrl: asset.thumbnailUrl,
                                category: asset.category ?? null,
                              },
                            ])
                            setIsOpen(false)
                          }}
                        >
                          <Image
                            data-testid={`asset-section-search-result-asset-item-image-${asset.id}`}
                            src={asset.thumbnailUrl || ''}
                            alt={asset.name || ''}
                            width={53}
                            height={40}
                            className="w-14 h-10 object-cover rounded bg-cf-grey-300"
                          />
                          <div
                            data-testid={`asset-section-search-result-asset-item-details-${asset.id}`}
                          >
                            <p
                              className="text-cf-grey-800"
                              dangerouslySetInnerHTML={{
                                __html: highlightMatch(asset.name ?? '', debouncedSearch),
                              }}
                            />
                            {asset?.category ? (
                              <p className="text-12 text-cf-grey-600">{asset.category}</p>
                            ) : null}
                          </div>
                        </div>
                      )
                    })}

                    <div
                      ref={loadMoreRef}
                      className="flex items-center justify-center text-cf-grey-500 text-xs"
                    >
                      {isFetchingNextPage ? <Spinner className="size-7 text-cf-purple-900" /> : ''}
                    </div>
                  </div>
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        </>
      ) : null}
    </div>
  )
}

export interface AssetSectionMobileContentProps {
  onClose: () => void
}

export function AssetSectionMobileContent({ onClose }: AssetSectionMobileContentProps) {
  const t = useTranslations('components.create_product_project_dialog.assets_section')
  const [searchText, setSearchText] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')

  const { assets: selectedAssets, setAssets: setSelectedAssets } = useProjectDraft()

  const debouncedSetSearch = React.useMemo(
    () => debounce((value: string) => setDebouncedSearch(value), 400),
    [],
  )

  React.useEffect(() => {
    debouncedSetSearch(searchText)
  }, [searchText, debouncedSetSearch])

  React.useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useListAssetsInfiniteQuery({
    text: debouncedSearch,
  })

  const listAssets = data?.pages.flatMap(page => page.data || []) ?? []
  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const node = loadMoreRef.current
    if (!node) {
      return
    }

    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    })

    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] sm:h-auto">
      <div className="sticky top-0 z-20 bg-cf-white pb-3 px-4">
        <SearchInput
          className="w-full"
          value={searchText}
          onChange={setSearchText}
          placeholder="Search for an asset"
        />
      </div>

      <p className="text-12 text-cf-grey-600 border-t border-[#1313151F] px-4 py-3">
        {t('assets_hint_text_mobile')}
      </p>

      <div className="flex-1 overflow-y-auto pb-2 px-2">
        {debouncedSearch && !isFetchingNextPage && listAssets.length === 0 ? (
          <div className="px-4 py-2 text-14 text-cf-grey-600">{t('assets_search_no_results')}</div>
        ) : null}
        {listAssets.map(asset => {
          const isSelected = selectedAssets.some(a => a.id === asset.id)
          return (
            <div
              key={asset.id}
              className={tw(
                'flex flex-row items-center gap-3 mb-3 px-2 py-1',
                isSelected
                  ? 'opacity-40 cursor-not-allowed pointer-events-none'
                  : 'cursor-pointer hover:bg-cf-purple-50',
              )}
              onClick={() => {
                if (isSelected) {
                  return
                }
                setSelectedAssets(prev => [
                  ...prev,
                  {
                    id: asset.id ?? '',
                    name: asset.name ?? '',
                    thumbnailUrl: asset.thumbnailUrl,
                    category: asset.category ?? null,
                  },
                ])
                onClose()
              }}
            >
              <Image
                src={asset.thumbnailUrl || ''}
                alt={asset.name || ''}
                width={53}
                height={40}
                className="w-14 h-10 object-cover rounded bg-cf-grey-300"
              />
              <div>
                <p className="text-cf-grey-800 font-semibold">{asset.name}</p>
                {asset?.category ? (
                  <p className="text-12 text-cf-grey-600">{asset.category}</p>
                ) : null}
              </div>
            </div>
          )
        })}
        <div
          ref={loadMoreRef}
          className="flex items-center justify-center text-cf-grey-500 text-xs"
        >
          {isFetchingNextPage ? <Spinner className="size-7 text-cf-purple-900" /> : ''}
        </div>
      </div>
    </div>
  )
}

export function AssetEmpty({ isPending }: { isPending: boolean }) {
  const t = useTranslations('components.create_product_project_dialog.assets_section')

  return (
    <div className="flex border border-dashed border-[#1313151F] bg-cf-grey-50 rounded-2 p-2 gap-3 items-center">
      {isPending ? (
        <SkeletonItem className="rounded-2 w-16 h-[42px]" />
      ) : (
        <div className="shrink-0 border border-cf-transparent-grey-1000-12 bg-cf-grey-50 rounded-2 w-16 h-[42px] flex justify-center items-center">
          <IconLinkOff className="text-cf-grey-400 !size-6" />
        </div>
      )}
      {isPending ? (
        <SkeletonItem className="rounded-2 w-[127px] h-[22px]" />
      ) : (
        <p className="text-cf-grey-400 text-14 text-left">{t('assets_empty_title')}</p>
      )}
    </div>
  )
}
